import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { EnvelopeIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Button, Input } from '../components/ui';
import { authService } from '../services/auth.service';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const ForgotPassword: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.forgotPassword(data.email);
      setIsSuccess(true);
    } catch (err: any) {
      // Show generic success message even on error (security - don't reveal if email exists)
      if (err.response?.status === 429) {
        setError(err.response?.data?.error || 'Too many requests. Please try again later.');
      } else {
        // Don't reveal if email exists or not
        setIsSuccess(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white dark:bg-secondary-900">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 py-12 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {/* Logo */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">CH</span>
              </div>
              <span className="font-display text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                Capture Health
              </span>
            </div>

            {!isSuccess ? (
              <>
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Reset your password</h2>
                <p className="mt-2 text-secondary-600 dark:text-secondary-400">
                  Enter your email and we'll send you a link to reset your password.
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-success-100 dark:bg-success-900/30">
                  <CheckCircleIcon className="w-8 h-8 text-success-600 dark:text-success-400" />
                </div>
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50 text-center">Check your email</h2>
                <p className="mt-2 text-secondary-600 dark:text-secondary-400 text-center">
                  If an account exists with that email, we've sent password reset instructions.
                </p>
              </>
            )}
          </div>

          {!isSuccess ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <Input
                label="Email address"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                leftIcon={<EnvelopeIcon className="h-5 w-5" />}
                error={errors.email?.message}
                {...register('email')}
              />

              {error && (
                <div className="p-3 rounded-lg bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoading}
              >
                Send reset link
              </Button>

              <Link
                to="/login"
                className="flex items-center justify-center text-sm font-medium text-secondary-600 dark:text-secondary-400 hover:text-primary-500"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to sign in
              </Link>
            </form>
          ) : (
            <div className="space-y-5">
              <p className="text-sm text-secondary-500 dark:text-secondary-400 text-center">
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  onClick={() => {
                    setIsSuccess(false);
                    setError(null);
                  }}
                  className="font-medium text-primary-500 hover:text-primary-600"
                >
                  try again
                </button>
              </p>

              <Link to="/login">
                <Button variant="secondary" className="w-full" size="lg">
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Back to sign in
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:block relative flex-1 gradient-mesh bg-secondary-50 dark:bg-secondary-800">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-lg text-center">
            <div className="w-24 h-24 mx-auto mb-8 rounded-2xl bg-white dark:bg-secondary-700 shadow-elevated flex items-center justify-center">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-2xl">CH</span>
              </div>
            </div>
            <h3 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
              Secure Password Recovery
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              We take security seriously. Your password reset link will expire in 24 hours.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
